// ============================================================
// TATHYA DMS - Audit Log UI
// Version: 2.6.0
// Date: 2026-04-23
// Change (v2.0): Full rendering rewrite per CEO review Clause 5.2.
//         - Each log is one human-readable sentence (ANX-1 format)
//         - Date shown as DD-Mon-YYYY HH:MM
//         - Action verbs humanized ("updated", "removed" vs UPDATE, DELETE)
//         - Reviewed entries greyed out instead of hidden (Clause 5.1)
//         - Added Undo button for misfired reviews
//         - Removed JSON before/after blocks; diff shown inline
//         - Removed raw entity_type #id footer (redundant now)
// Change (v2.1): Display-time column-ID → label resolution, so
//         audit entries written before the v1.1 middleware (which
//         still have numeric keys like {"7": "abc"}) also render
//         as "Customer Name" instead of "7". No data migration.
// Change (v2.2): Row and column numbers displayed in each entry
//         ("Row 5, Column 6 (Product Price) updated from..."),
//         using the frozen position context stored by the v1.2
//         middleware. Historical entries without _pos fall back
//         to the v2.1 format gracefully.
// Change (v2.6): The "Row N" clickable chip + row expander now
//         work on PDF_UPLOAD, PDF_REPLACE, and PDF_DELETE audit
//         entries too — CEO directive 29-May, ANX-1 #2.c+d.
//         A position context is synthesised from the PDF payload
//         (row_position_1based + column_label) so rowLabel() works
//         the same way as for INSERT/UPDATE/DELETE row entries.
// Change (v2.5): The column(s) actually changed by the audit
//         entry are highlighted in red inside the row-expander
//         panel, so the reviewer can spot the change at a glance.
//         The diff comes from log.before_value/after_value with the
//         same resolveKeys() rehydration the sentence uses.
// Change (v2.4): Each audit entry's "Row N" label is now
//         clickable. Clicking expands an inline panel under the
//         entry showing all current cell values for that row
//         (with deleted-row fallback to the audit's before/after
//         snapshot). Pairs with backend v1.4 per-role review
//         filtering and the new GET .../rows/:rowId/full endpoint.
// Change (v2.3): Review click now removes the entry from the list
//         instantly (no page refresh needed). Undo button and
//         unreview flow removed entirely — recovery is SQL-only
//         per the v1.3 backend behavior. Matches operator's
//         "clean-inbox" mental model: one click, entry gone.
// ============================================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuditLogs, reviewLog, getDatabase, getRowFull } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const HIDDEN_ACTIONS = new Set(['COLUMNS_REORDERED']);

// ── Formatting helpers ────────────────────────────────────────
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Matches ANX-1 reference: "30-Aug-2025 11:33"
const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}-${MONTHS[d.getMonth()]}-${d.getFullYear()} ` +
         `${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Human size for PDF metadata. 12345 → "12 KB", 1234567 → "1.2 MB"
const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Build a "Row N · Column Name" suffix from a PDF audit payload.
// Falls back gracefully when fields are missing — old audit rows
// (written before row_position_1based was captured) just won't show
// the row part. New rows show both. Returns '' when nothing is
// available so callers can append it unconditionally.
//
// v2.6 (29-May): now accepts an optional (rowId, onRowClick) pair. When
// both are supplied AND the payload has a row position, the "Row N"
// part is rendered as a clickable chip (same as INSERT/UPDATE/DELETE)
// via the shared rowLabel() helper. Returns JSX in that case;
// otherwise still returns a plain string for backward compat with any
// pre-v2.6 caller.
const formatPdfLocation = (payload, rowId, onRowClick) => {
  if (!payload) return '';
  const row = payload.row_position_1based;
  const col = payload.column_label;
  const colPos = payload.column_position;
  if (row == null && !col) return '';
  const colText = col
    ? (colPos != null ? `Col ${colPos} (${col})` : col)
    : null;
  // Clickable Row N when a row click handler is supplied (v2.6 behaviour).
  if (row != null && rowId && onRowClick) {
    const rowChip = rowLabel({ row }, rowId, onRowClick);
    return (
      <> — {rowChip}{colText ? <> · {colText}</> : null}</>
    );
  }
  // Plain string path — same output shape as v2.5.
  const parts = [];
  if (row != null) parts.push(`Row ${row}`);
  if (colText) parts.push(colText);
  return parts.length ? ` — ${parts.join(' · ')}` : '';
};

// Quote a cell value for display. null/undefined/'' all render as "—"
// so the sentence still reads cleanly: "updated from — to 'Acme'".
const q = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  return `'${String(v)}'`;
};

// Build the pairs array: [{ column, before, after }, ...]
// from the before_value / after_value JSON shapes.
// Reserved keys (prefixed with "_", e.g. "_pos") are filtered out —
// those carry metadata not display content.
const diffPairs = (before, after) => {
  const keys = new Set([
    ...Object.keys(before || {}),
    ...Object.keys(after  || {})
  ]);
  return Array.from(keys)
    .filter(k => !k.startsWith('_'))
    .map(k => ({
      column: k,
      before: before && before[k] !== undefined ? before[k] : null,
      after:  after  && after[k]  !== undefined ? after[k]  : null
    }));
};

// Extract the frozen position context embedded in an audit entry's
// after_value under the reserved `_pos` key. Returns { row, cols }
// or null if no context was stored (older entries).
const getPosContext = (after) => {
  if (!after || typeof after !== 'object') return null;
  return after._pos || null;
};

// Format "Row 5" — returns empty string when no row position known
// (historical entries) so the existing sentence reads naturally.
const rowLabel = (pos, rowId, onRowClick) => {
  if (!pos || typeof pos.row !== 'number') return '';
  const text = `Row ${pos.row}`;
  if (!rowId || !onRowClick) return text;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onRowClick(rowId); }}
      style={styles.rowChip}
      title="Show full row contents"
    >{text}</button>
  );
};

// Format "Column 6 (Product Price)" for a given column label, using
// the position map in the pos context. If no position is known for
// this column (older entry or columns changed since), fall back to
// just the column name.
const colLabel = (columnName, pos) => {
  const n = pos?.cols?.[columnName];
  if (typeof n === 'number') return `Column ${n} (${columnName})`;
  return columnName;
};

// Resolve numeric-keyed payloads to label-keyed ones at display time.
// Needed for audit entries written BEFORE the v1.1 middleware (which
// resolves at write time) — those entries have raw column IDs like
// {"7": "abc"} stored. For entries already using labels, this is a
// no-op. Unknown IDs (column since deleted) become "col_<id>" rather
// than disappearing — same fallback the backend uses.
const resolveKeys = (payload, columnMap) => {
  if (!payload || typeof payload !== 'object') return payload;
  if (!columnMap) return payload;
  const keys = Object.keys(payload);
  if (keys.length === 0) return payload;
  // Only transform if ALL keys look like numeric IDs — guards against
  // partial transformation of a mixed payload (shouldn't happen, but).
  const allNumeric = keys.every(k => /^\d+$/.test(k));
  if (!allNumeric) return payload;
  const out = {};
  for (const [k, v] of Object.entries(payload)) {
    out[columnMap[k] || `col_${k}`] = v;
  }
  return out;
};

// Render the expandable row-content panel for one audit entry.
// Three cases: (1) row fetched OK, show all columns; (2) row deleted,
// fall back to the audit's before/after snapshot; (3) nothing to show.
const renderRowPanel = (log, payload, columnMap) => {
  // Set of column labels that this audit entry actually changed.
  // Resolved through columnMap so pre-v1.1 numeric-keyed payloads
  // also match against the current label-keyed column dump.
  const changed = new Set();
  const bv = resolveKeys(log.before_value, columnMap) || {};
  const av = resolveKeys(log.after_value,  columnMap) || {};
  Object.keys(bv).filter(k => !k.startsWith('_')).forEach(k => changed.add(k));
  Object.keys(av).filter(k => !k.startsWith('_')).forEach(k => changed.add(k));
  // PDF actions store the touched column under `column_label`, not
  // as a keyed cell value — pull that in too so PDF expansions
  // highlight the file column.
  if (av.column_label) changed.add(av.column_label);
  if (bv.column_label) changed.add(bv.column_label);
  if (!payload) return <span style={styles.dim}>No data to show.</span>;

  if (payload.columns && Array.isArray(payload.columns)) {
    const { row, columns } = payload;
    return (
      <div>
        <div style={styles.expanderHeader}>
          Row {row && row.position_1based != null ? row.position_1based : '?'} · current state
        </div>
        <table style={styles.expanderTable}>
          <tbody>
            {columns.map(c => {
              const isChanged = changed.has(c.label);
              return (
                <tr key={c.id} style={isChanged ? styles.expanderRowChanged : undefined}>
                  <td style={{ ...styles.expanderColName, ...(isChanged ? styles.expanderTextChanged : {}) }}>
                    Col {c.position + 1} ({c.label})
                    {isChanged && <span style={styles.changedBadge}> ● changed</span>}
                  </td>
                  <td style={{ ...styles.expanderColVal, ...(isChanged ? styles.expanderTextChanged : {}) }}>
                    {c.file
                      ? <em>{c.file.original_name}</em>
                      : (c.value == null || c.value === ''
                          ? <span style={styles.dim}>—</span>
                          : String(c.value))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (payload.error === 'not_found') {
    const snap = log.before_value || log.after_value || {};
    const keys = Object.keys(snap).filter(k => !k.startsWith('_'));
    if (keys.length === 0) {
      return <span style={styles.dim}>Row no longer exists; no snapshot in this audit entry.</span>;
    }
    return (
      <div>
        <div style={styles.expanderHeader}>
          Row no longer exists — snapshot from this audit entry
        </div>
        <table style={styles.expanderTable}>
          <tbody>
            {keys.map(k => {
              const isChanged = changed.has(k);
              return (
                <tr key={k} style={isChanged ? styles.expanderRowChanged : undefined}>
                  <td style={{ ...styles.expanderColName, ...(isChanged ? styles.expanderTextChanged : {}) }}>
                    {k}
                    {isChanged && <span style={styles.changedBadge}> ● changed</span>}
                  </td>
                  <td style={{ ...styles.expanderColVal, ...(isChanged ? styles.expanderTextChanged : {}) }}>
                    {snap[k] == null || snap[k] === ''
                      ? <span style={styles.dim}>—</span>
                      : String(snap[k])}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return <span style={styles.dim}>No data to show.</span>;
};

// ── Sentence renderer — picks template per action ─────────────
// Returns a React node, not a plain string, because UPDATE can
// span multiple lines (one per changed column).
// `columnMap` is { columnId: columnLabel } used to rehydrate
// historical entries that stored numeric keys.
const renderSentence = (log, columnMap, onRowClick) => {
  const { action } = log;
  const before_value = resolveKeys(log.before_value, columnMap);
  const after_value  = resolveKeys(log.after_value,  columnMap);
  const pos = getPosContext(after_value);
  const rowId = (log.entity_type === 'row') ? log.entity_id : null;
  const rowPart = rowLabel(pos, rowId, onRowClick);

  switch (action) {
    case 'INSERT': {
      // "Row 5 added: Customer Name='Acme Ltd', Amount='5000'"
      const pairs = diffPairs(null, after_value);
      const prefix = rowPart ? `${rowPart} added` : 'New row added';
      if (pairs.length === 0) return prefix;
      const parts = pairs.map(p => `${colLabel(p.column, pos)}=${q(p.after)}`).join(', ');
      return <><strong>{prefix}:</strong> {parts}</>;
    }

    case 'UPDATE': {
      // Single change:  "Row 5, Column 6 (Product Price) updated from '2500' to '2501'"
      // Multiple:       "Row 5 updated:  - Column 6 (Product Price) from ... to ..."
      const pairs = diffPairs(before_value, after_value);
      if (pairs.length === 0) return 'Row updated (no changes detected)';
      if (pairs.length === 1) {
        const p = pairs[0];
        const head = rowPart
          ? <>{rowPart}, <strong>{colLabel(p.column, pos)}</strong></>
          : <strong>{colLabel(p.column, pos)}</strong>;
        return <>{head} updated from {q(p.before)} to {q(p.after)}</>;
      }
      return (
        <span>
          <strong>{rowPart ? `${rowPart} updated` : 'Row updated'}:</strong>
          <ul style={styles.changeList}>
            {pairs.map(p => (
              <li key={p.column}>
                <strong>{colLabel(p.column, pos)}</strong> from {q(p.before)} to {q(p.after)}
              </li>
            ))}
          </ul>
        </span>
      );
    }

    case 'DELETE': {
      // "Row 5 removed: Customer Name='Acme Ltd', Amount='5000'"
      // DELETE doesn't have after_value from the action itself, but
      // the middleware still writes _pos into it when row_position
      // was supplied. Re-check on before_value just in case.
      const pairs = diffPairs(before_value, null);
      const prefix = rowPart ? `${rowPart} removed` : 'Row removed';
      if (pairs.length === 0) return prefix;
      const parts = pairs.map(p => `${colLabel(p.column, pos)}=${q(p.before)}`).join(', ');
      return <><strong>{prefix}:</strong> {parts}</>;
    }

    case 'PDF_UPLOAD': {
      const f = after_value?.filename || 'unnamed file';
      const s = after_value?.size ? ` (${formatSize(after_value.size)})` : '';
      // rowId resolves through getRowFull on click (v2.4 wiring) — works
      // because the backend stores entity_type='row', entity_id=row.id
      // for PDF_UPLOAD entries.
      const pdfRowId = (log.entity_type === 'row') ? log.entity_id : null;
      const loc = formatPdfLocation(after_value, pdfRowId, onRowClick);
      return <><strong>PDF uploaded:</strong> {f}{s}{loc}</>;
    }

    case 'PDF_REPLACE': {
      const oldName = before_value?.filename;
      const newName = after_value?.filename || 'unnamed file';
      const newSize = after_value?.size ? ` (${formatSize(after_value.size)})` : '';
      const pdfRowId = (log.entity_type === 'row') ? log.entity_id : null;
      const loc = formatPdfLocation(after_value, pdfRowId, onRowClick);
      if (oldName) {
        return <><strong>PDF replaced:</strong> {oldName} → {newName}{newSize}{loc}</>;
      }
      return <><strong>PDF replaced:</strong> {newName}{newSize}{loc}</>;
    }

    case 'PDF_DELETE': {
      const f = before_value?.filename;
      const s = before_value?.size ? ` (${formatSize(before_value.size)})` : '';
      // PDF_DELETE stores the location info in before_value (the file was
      // removed; after_value is null). entity_type='row' / entity_id=row.id
      // is still on the audit row, so the chip + expander still work.
      const pdfRowId = (log.entity_type === 'row') ? log.entity_id : null;
      const loc = formatPdfLocation(before_value, pdfRowId, onRowClick);
      if (f) return <><strong>PDF deleted:</strong> {f}{s}{loc}</>;
      return <><strong>PDF deleted</strong>{loc}</>;
    }

    case 'LINK_GENERATED':
      return <strong>Shareable link generated</strong>;

    case 'LINK_REVOKED':
      return <strong>Shareable link revoked</strong>;

    case 'LINK_ACCESSED':
      return <strong>Shareable link accessed</strong>;

    case 'LINK_ACCESS_DENIED':
      return <strong>Shareable link access denied</strong>;

    case 'USER_CREATED': {
      const email = after_value?.email || '';
      return <><strong>User created:</strong> {email}</>;
    }

    case 'USER_DEACTIVATED': {
      const email = before_value?.email || after_value?.email || '';
      return <><strong>User deactivated:</strong> {email}</>;
    }

    case 'USER_REACTIVATED': {
      const email = after_value?.email || '';
      return <><strong>User reactivated:</strong> {email}</>;
    }

    case 'PASSWORD_RESET':
      return <strong>Password reset</strong>;

    case 'LOGIN':
      return <strong>Signed in</strong>;

    case 'COLUMNS_REORDERED': {
      // after_value is { order: [colId, colId, ...] } written by the
      // reorder endpoint. We show the reordered labels using columnMap
      // (same lookup the resolveKeys helper uses), falling back to
      // col_<id> for any column that has since been deleted.
      const order = log.after_value?.order || [];
      const labels = order
        .map(id => (columnMap && columnMap[id]) || `col_${id}`)
        .join(' → ');
      if (!labels) return <strong>Columns reordered</strong>;
      return <><strong>Columns reordered:</strong> {labels}</>;
    }

    default:
      // Fallback for any action we haven't templated.
      // Keeps the log readable rather than showing nothing.
      return <><strong>{action}</strong></>;
  }
};

// Humanized labels for the filter buttons (matches the verbs
// used in the sentences themselves).
const ACTION_LABEL = {
  INSERT:          'Added',
  UPDATE:          'Updated',
  DELETE:          'Removed',
  PDF_UPLOAD:      'PDF uploaded',
  PDF_REPLACE:     'PDF replaced',
  PDF_DELETE:      'PDF deleted',
  LINK_GENERATED:  'Link generated',
  LINK_REVOKED:    'Link revoked',
  LINK_ACCESSED:   'Link accessed',
  LINK_ACCESS_DENIED: 'Link denied',
  USER_CREATED:    'User created',
  USER_DEACTIVATED:'User deactivated',
  USER_REACTIVATED:'User reactivated',
  PASSWORD_RESET:  'Password reset',
  LOGIN:           'Signed in',
  COLUMNS_REORDERED: 'Columns reordered'
};

const AuditLog = () => {
  const { org_slug, db_id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [database, setDatabase] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [busyId, setBusyId] = useState(null);
  const [expandedLogId, setExpandedLogId] = useState(null);
  const [rowCache, setRowCache] = useState({});
  const [rowLoadingId, setRowLoadingId] = useState(null);
  // total/hasMore come from the server (fix — 14-Jul-2026). Each
  // page is capped at 500 by the backend; a database can have more
  // unreviewed entries than that, so the true count and "is there
  // another page" must come from the API, not from logs.length —
  // otherwise reviewing a full page looks like it finished the job
  // when hundreds more are still sitting unreviewed underneath.
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [dbRes, logRes] = await Promise.all([
          getDatabase(org_slug, db_id),
          getAuditLogs(org_slug, db_id)
        ]);
        setDatabase(dbRes.data);
        setLogs(logRes.data.logs);
        setTotal(logRes.data.total);
        setHasMore(logRes.data.hasMore);
      } catch (err) {
        setError('Failed to load audit logs');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [org_slug, db_id]);

  const handleLoadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await getAuditLogs(org_slug, db_id, { offset: logs.length });
      setLogs(prev => [...prev, ...res.data.logs]);
      setTotal(res.data.total);
      setHasMore(res.data.hasMore);
    } catch (err) {
      setError('Failed to load more audit logs');
    } finally {
      setLoadingMore(false);
    }
  };

  // v2.4: row click — toggle expander; on first open, fetch the row.
  // Falls back to the audit entry's snapshot if the row was deleted.
  const handleRowClick = async (log) => {
    if (expandedLogId === log.id) { setExpandedLogId(null); return; }
    setExpandedLogId(log.id);
    if (rowCache[log.id]) return;
    if (log.entity_type !== 'row' || !log.entity_id) return;
    setRowLoadingId(log.id);
    try {
      const res = await getRowFull(org_slug, db_id, log.entity_id);
      setRowCache(prev => ({ ...prev, [log.id]: res.data }));
    } catch (err) {
      setRowCache(prev => ({ ...prev, [log.id]: { error: 'not_found' } }));
    } finally {
      setRowLoadingId(null);
    }
  };

  // v2.3: clicking Review removes the entry from the list instantly.
  // The backend (audit.js v1.3) also filters reviewed entries from
  // subsequent GETs, so the entry stays gone across refreshes. There
  // is deliberately no Undo button — mis-click recovery is SQL-only
  // per the operator's explicit "keep it simple" preference.
  const handleReview = async (log) => {
    setBusyId(log.id);
    try {
      await reviewLog(org_slug, db_id, log.id);
      setLogs(prev => prev.filter(l => l.id !== log.id));
      setTotal(prev => Math.max(prev - 1, 0));
    } catch (err) {
      setError('Failed to mark as reviewed');
    } finally {
      setBusyId(null);
    }
  };

  // v2.3: The backend now filters reviewed entries out of the
  // API response entirely, and handleReview removes entries from
  // local state on click. So the only client-side filter left here
  // is hiding the always-noisy COLUMNS_REORDERED action.
  const visibleLogs = logs.filter(log => {
    if (HIDDEN_ACTIONS.has(log.action)) return false;
    return true;
  });
  const filteredLogs = visibleLogs.filter(log => {
    if (filter === 'all') return true;
    return log.action === filter;
  });

  const uniqueActions = [...new Set(visibleLogs.map(l => l.action))];

  // Build { columnId: columnLabel } so the renderer can resolve
  // numeric keys in pre-Step-2 audit entries. Current-column names
  // are used — if a column has been renamed, old entries will show
  // the current name, not the name at time-of-action. Acceptable
  // tradeoff vs. showing "7" to a user forever.
  const columnMap = {};
  (database?.DbColumns || []).forEach(c => { columnMap[String(c.id)] = c.label; });

  if (loading) return <div style={styles.center}>Loading audit logs...</div>;
  if (error)   return <div style={styles.center}>{error}</div>;

  return (
    <div>
      <div style={styles.header}>
        <button className="btn btn-secondary"
          onClick={() => navigate(`/${org_slug}/databases/${db_id}`)}
          style={{ marginBottom: '8px', fontSize: '12px' }}>
          ← Back to {database?.name}
        </button>
        <h1 style={styles.title}>Audit Log</h1>
        <p style={styles.subtitle}>
          {database?.db_number} · {database?.name} ·{' '}
          {total} unreviewed{logs.length < total ? ` (${logs.length} loaded)` : ''}
        </p>
      </div>

      <div style={styles.filterBar}>
        <button
          className={`btn ${filter === 'all' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setFilter('all')}
          style={styles.filterBtn}>
          All ({visibleLogs.length})
        </button>
        {uniqueActions.map(action => (
          <button key={action}
            className={`btn ${filter === action ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setFilter(action)}
            style={styles.filterBtn}>
            {ACTION_LABEL[action] || action}{' '}
            ({visibleLogs.filter(l => l.action === action).length})
          </button>
        ))}
      </div>

      {filteredLogs.length === 0 ? (
        <div className="card" style={styles.empty}>
          {filter === 'all' ? 'No entries yet' : `No ${ACTION_LABEL[filter] || filter} entries`}
        </div>
      ) : (
        <ul style={styles.logList}>
          {filteredLogs.map(log => {
            const user = log.user || log.User || {};
            return (
              <li key={log.id} style={styles.logRow}>
                <div style={styles.logBody}>
                  <div style={styles.sentence}>
                    {renderSentence(log, columnMap, (rowId) => handleRowClick(log))}
                  </div>
                  {expandedLogId === log.id && (
                    <div style={styles.rowExpander}>
                      {rowLoadingId === log.id
                        ? <span style={styles.dim}>Loading row…</span>
                        : renderRowPanel(log, rowCache[log.id], columnMap)}
                    </div>
                  )}
                  <div style={styles.meta}>
                    on <strong>{formatDate(log.created_at)}</strong>
                    {user.email ? <> by <strong>{user.email}</strong></> : null}
                  </div>
                </div>
                <div style={styles.actions}>
                  <button className="btn btn-secondary"
                    onClick={() => handleReview(log)}
                    disabled={busyId === log.id}
                    style={styles.reviewBtn}>
                    {busyId === log.id ? '…' : '✓ Reviewed'}
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {hasMore && (
        <div style={styles.loadMoreWrap}>
          <button className="btn btn-secondary"
            onClick={handleLoadMore}
            disabled={loadingMore}
            style={styles.filterBtn}>
            {loadingMore ? 'Loading…' : `Load more (${total - logs.length} remaining)`}
          </button>
        </div>
      )}
    </div>
  );
};

const styles = {
  header: { marginBottom: '20px' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1B3A5C', margin: 0 },
  subtitle: { color: '#595959', fontSize: '14px', marginTop: '4px' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '300px', color: '#2E75B6', fontSize: '16px' },
  filterBar: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' },
  filterBtn: { padding: '5px 12px', fontSize: '12px' },
  empty: { textAlign: 'center', padding: '40px', color: '#595959', fontSize: '14px' },
  loadMoreWrap: { textAlign: 'center', marginTop: '16px' },

  logList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    background: 'white',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
  },
  logRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '12px 16px',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.15s'
  },
  logBody: {
    flex: 1,
    minWidth: 0
  },
  sentence: {
    fontSize: '14px',
    color: '#1B3A5C',
    lineHeight: '1.5',
    wordBreak: 'break-word'
  },
  changeList: {
    margin: '4px 0 0 0',
    paddingLeft: '20px',
    fontSize: '13px',
    color: '#1B3A5C'
  },
  meta: {
    fontSize: '12px',
    color: '#595959',
    marginTop: '4px'
  },
  actions: {
    flexShrink: 0
  },
  reviewBtn: { padding: '4px 10px', fontSize: '12px' },
  rowChip: {
    background: '#E8F0F8',
    border: '1px solid #B6CCE0',
    color: '#1B3A5C',
    padding: '0px 8px',
    borderRadius: '12px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'inline-block',
    lineHeight: '20px'
  },
  rowExpander: {
    marginTop: '8px',
    padding: '10px 12px',
    background: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: '6px'
  },
  expanderHeader: {
    fontSize: '12px',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px'
  },
  expanderTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px'
  },
  expanderColName: {
    padding: '4px 8px 4px 0',
    color: '#475569',
    fontWeight: '500',
    verticalAlign: 'top',
    width: '35%',
    whiteSpace: 'nowrap'
  },
  expanderColVal: {
    padding: '4px 0',
    color: '#1B3A5C',
    wordBreak: 'break-word'
  },
  dim: { color: '#94A3B8', fontStyle: 'italic' },
  expanderRowChanged: {
    background: '#FEE2E2'
  },
  expanderTextChanged: {
    color: '#B91C1C',
    fontWeight: '600'
  },
  changedBadge: {
    fontSize: '11px',
    color: '#B91C1C',
    marginLeft: '6px'
  }
};

export default AuditLog;
